/**
 * Sportsbook_live_data subscription tracking.
 */
class sbLiveDataSubscriptionStoreClass {
    /**
     * @var {map} #subscriptions { entityIdentifier -> <object>subscription_data }
     * An entity could be a gtp_betbuilder combination selection (e.g. bb_123_456). The
     * subscription data should be the entire payload needed to send to SDL, except for the "action" property.
     * <Map>( type => <Map>subscriptions )
     */
    #subscriptionsByType = new Map();

    /**
     * @var {map} #subscriptionCounts { entityIdentifier -> <int>count }
     */
    #subscriptionCountsByType = new Map();

    /**
     * Generates a unique cache key based on subscription type and selections.
     * @param {string} type The subscription type identifier (same as used in the wsMessage)
     * @param {Array} selections Array of selection identifiers to be sorted and joined (this may
     * need updating if your key relies on different entities)
     * @returns {string} A composite key. e.g. `gtp_betbuilder_100_123_999`
     */
    #getKey(type, selections) {
        return `${type}_${selections.toSorted().join('_')}`;
    }

    /**
     * Retrieve sunbscriptions for a specified type
     * @param {string} type e.g. gtp_betbuilder
     * @returns {map} subset of this.#subscriptionsByType
     */
    getSubscriptionsByType(type) {
        const subscriptions = this.#subscriptionsByType.get(type);
        if (subscriptions) {

            return subscriptions;
        }

        const emptySubscriptions = new Map();
        this.#subscriptionsByType.set(type, emptySubscriptions);

        return emptySubscriptions;
    }

    /**
     * Retrieve sunbscription counts for a specified type
     * @param {string} type e.g. gtp_betbuilder
     * @returns {map} subset of this.#subscriptionCountsByType
     */
    #getSubscriptionCountsByType(type) {
        const counts = this.#subscriptionCountsByType.get(type);
        if (counts) {

            return counts;
        }

        const emptyCounts = new Map();
        this.#subscriptionCountsByType.set(type, emptyCounts);

        return emptyCounts;
    }

    /**
     * Clear subscription data relating to a specified type
     * @param {string} type e.g. gtp_betbuilder
     * @returns {void}
     */
    #clearSubscriptionsByType(type) {
        this.#subscriptionsByType.set(type, new Map());
        this.#subscriptionCountsByType.set(type, new Map());
    }

    /**
     * Increment subscription count for a given selection/combination
     * @param {string} type selection/combination type, e.g. gtp_betbuilder
     * @param {mixed} selectionIds array of ids that make up this selection/combination
     * @returns {number} new count
     */
    #incrementSubscription(type, selectionIds) {
        const subscriptionKey = this.#getKey(type, selectionIds);
        const subscriptionsMap = this.getSubscriptionsByType(type);
        const subscriptionCountsMap = this.#getSubscriptionCountsByType(type);

        const existingSubscription = subscriptionsMap.get(subscriptionKey);
        const newCount = existingSubscription ? subscriptionCountsMap.get(subscriptionKey) + 1 : 1;
        if (!existingSubscription) {
            subscriptionsMap.set(subscriptionKey, selectionIds);
        }

        subscriptionCountsMap.set(subscriptionKey, newCount);

        return newCount;
    }

    /**
     * Decrement subscription count for a given selection/combination
     * @param {string} type selection/combination type, e.g. gtp_betbuilder
     * @param {mixed} selectionIds array of ids that make up this selection/combination
     * @returns {number} new count
     */
    #decrementSubscription(type, selectionIds) {
        const subscriptionKey = this.#getKey(type, selectionIds);
        const subscriptionsMap = this.getSubscriptionsByType(type);
        const subscriptionCountsMap = this.#getSubscriptionCountsByType(type);

        const existingSubscription = subscriptionsMap.get(subscriptionKey);
        if (!existingSubscription) {
            // eslint-disable-next-line no-console
            console.warn(`Warning: sbLiveDataSubscriptionStoreClass::decrementSubscription() - key "${subscriptionKey}" does not exist`);

            return 0;
        }

        const newCount = subscriptionCountsMap.get(subscriptionKey) - 1;
        if (newCount <= 0) {
            subscriptionsMap.delete(subscriptionKey);
            subscriptionCountsMap.delete(subscriptionKey);

            return 0;
        }

        subscriptionCountsMap.set(subscriptionKey, newCount);

        return newCount;
    }

    /**
     * Process a subscribe request by incrementing subscription counts for a specified type
     * @param {object} payload the wsMessage payload
     * @param {string} payload.type e.g. gtp_betbuilder
     * @param {array} payload.selectionIdGroups array of arrays of selectionIds
     * @returns {object} filtered payload that should be sent via websocket
     */
    subscribe(payload) {
        const { type, selectionIdGroups } = payload;

        const filteredPayload = [];
        selectionIdGroups.forEach(selectionIds => {
            const newSubscriptionCount = this.#incrementSubscription(type, selectionIds);
            if (newSubscriptionCount) {
                filteredPayload.push(selectionIds);
            }
        });

        if (!filteredPayload.length) {

            return false;
        }

        return filteredPayload;
    }

    /**
     * Process an usubscribe request by decrementing subscription counts for a specified type or by
     * clearing all subscription counts if the payload.selectionIdGroups is null
     * @param {object} payload the wsMessage payload
     * @param {string} payload.type e.g. gtp_betbuilder
     * @param {array|null} payload.selectionIdGroups array of arrays of selectionIds or nul to unsubscribe from all
     * @returns {object} filtered payload that should be sent via websocket
     */
    unsubscribe(payload) {
        const { type, selectionIdGroups } = payload;
        if (selectionIdGroups === null) {
            // pass null to unregister from all in SLD
            this.#clearSubscriptionsByType(type);

            return selectionIdGroups;
        }

        const filteredPayload = [];
        selectionIdGroups.forEach(selectionIds => {
            const newSubscriptionCount = this.#decrementSubscription(type, selectionIds);

            if (!newSubscriptionCount) {
                filteredPayload.push(selectionIds);
            }
        });

        if (!filteredPayload.length) {

            return false;
        }

        return filteredPayload;
    }

    /**
     * Get a list if current subscriptions for a subscription type.
     * This list is generally used in an onConnection callback to resubscribe in the event of a
     * dropped and reestablished websocket connection
     * Note the use of an arrow function to preserve the 'this' binding
     * @returns {array} an array of current subscription data
     */
    getSubscriptions = () => {
        const subscriptionPayloads = {};
        this.#subscriptionsByType.keys().forEach(
            key => {
                subscriptionPayloads[key] = Array.from(this.#subscriptionsByType.get(key).values());
            }
        );

        return subscriptionPayloads;
    };

    /**
     * Clear all subscription data and subscription counts, e.g. when a websocket connection is closed
     * This list is generally used in an onKill callback
     * Note the use of an arrow function to preserve the 'this' binding
     * @returns {void}
     */
    clearSubscriptions = () => {
        this.#subscriptionsByType = new Map();
        this.#subscriptionCountsByType = new Map();
    };
}

const sbLiveDataSubscriptionStore = new sbLiveDataSubscriptionStoreClass();

export default sbLiveDataSubscriptionStore;
